import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import {
  UpdateAnimation,
  shouldShowUpdateAnimation,
  triggerUpdateRefresh,
} from '@/components/UpdateAnimation';
import { useAuth } from '@/context/AuthContext';

interface UpdateAnimationContextValue {
  /**
   * Trigger an update refresh - sets flag and reloads page immediately.
   * The update animation will display on the new page load while content loads.
   */
  triggerRefresh: () => void;
  /**
   * Whether the update animation is currently showing.
   */
  isShowingUpdateAnimation: boolean;
}

const UpdateAnimationContext = createContext<UpdateAnimationContextValue | null>(null);

interface UpdateAnimationProviderProps {
  children: ReactNode;
}

/**
 * Provider that manages the update animation.
 * On page load, checks if an update animation should be shown.
 * The animation displays while the app loads, then fades to reveal content.
 */
export function UpdateAnimationProvider({ children }: UpdateAnimationProviderProps) {
  const { authState } = useAuth();

  // Don't show animation on admin page
  const [showAnimation, setShowAnimation] = useState(() => {
    // Check if on admin page inside callback to avoid closure issues
    const onAdminPage =
      typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
    if (onAdminPage) {
      // Still consume the flag so it doesn't persist
      shouldShowUpdateAnimation();
      return false;
    }
    return shouldShowUpdateAnimation();
  });

  // Check if on admin page (use window.location to avoid hook issues)
  const isAdminPage =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

  const isReady = authState.status !== 'loading';

  const handleAnimationComplete = useCallback(() => {
    setShowAnimation(false);
  }, []);

  const triggerRefresh = useCallback(() => {
    triggerUpdateRefresh();
  }, []);

  // Never show animation on admin page
  const shouldRenderAnimation = showAnimation && !isAdminPage;

  const value = useMemo(
    () => ({
      triggerRefresh,
      isShowingUpdateAnimation: shouldRenderAnimation,
    }),
    [triggerRefresh, shouldRenderAnimation]
  );

  return (
    <UpdateAnimationContext.Provider value={value}>
      {children}
      {shouldRenderAnimation && (
        <UpdateAnimation isReady={isReady} onComplete={handleAnimationComplete} />
      )}
    </UpdateAnimationContext.Provider>
  );
}

/**
 * Hook to access update animation functionality.
 * Call triggerRefresh() to reload the page with the update animation.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useUpdateAnimation(): UpdateAnimationContextValue {
  const context = useContext(UpdateAnimationContext);
  if (!context) {
    throw new Error('useUpdateAnimation must be used within UpdateAnimationProvider');
  }
  return context;
}
